import { isEqual, map, merge } from 'lodash';

import { GAEvent } from '@swf/core/analytics/utils';
import type { RequestError } from '@swf/core/api/request';
import type { Currency } from '@swf/core/enums';
import type {
  BettableOpportunities,
  OpportunityStatus,
  SingleAvailableOpportunity,
  SingleOpportunity
} from '@swf/core/models/sports/types/betSlip';
import type {
  AvailableCashOutQuote,
  CashOutQuote
} from '@swf/core/models/sports/types/cashoutQuotes';
import type { MyBets } from '@swf/core/models/sports/types/myBets';
import { isAvailableOpportunity } from '@swf/core/models/sports/utils/betSlip/isOpportunity';
import type { TrackingContextValue } from '@swf/core/providers/TrackingContext';
import { buttonClick } from '@swf/generated/analytics/buttonClick';
import type { ButtonClick } from '@swf/generated/analytics/buttonClick';
import {
  sportsBet,
  sportsEvent,
  sportsMarket,
  sportsSelection,
  widget as widgetEntity
} from '@swf/generated/analytics/entities';
import type { Entity, SportsBet } from '@swf/generated/analytics/entities';
import { errorView } from '@swf/generated/analytics/errorView';
import { widgetView } from '@swf/generated/analytics/widgetView';
import type { MyBet } from '@swf/generated/types/api/sports';
import type { EventId } from '@swf/sports/types/event';
import type { MarketId } from '@swf/sports/types/market';
import type { Odds } from '@swf/sports/types/odds';
import type { PlaceBetResp, PlaceBetResult } from '@swf/sports/types/place-bet';
import { RunnerStatus } from '@swf/sports/types/runner';
import type { RunnerId } from '@swf/sports/types/runner';
import type { SelectionId } from '@swf/sports/types/selection';
import getRunnerStatus from '@swf/sports/utils/getRunnerStatus';
import isOddsBoostMarket from '@swf/sports/utils/isOddsBoostMarket';
import mergeWithTrackingContextValue from '@swf/sports/utils/mergeWithTrackingContextValue';

import {
  createMultipleEntities,
  createSingleEntities
} from '../../../widgets/sports-bet-slip-widget/utils/createEntities';

export type BetslipTrackingArg = ButtonClick & { entities: Entity[] };

interface AddedToBetslipProps {
  eventId: EventId;
  startTime: string;
  marketId: MarketId;
  selectionId: SelectionId;
  odds?: Odds;
  runnerStatus: RunnerStatus;
}

export const addedToBetslip = (
  context: TrackingContextValue,
  { eventId, startTime, marketId, selectionId, odds, runnerStatus }: AddedToBetslipProps
) => {
  context = mergeWithTrackingContextValue(context, [
    sportsEvent({ eventId, eventStartTime: new Date(startTime).getTime() }),
    sportsMarket({ marketId: Number(marketId), marketInPlay: false, marketIsOddsBoost: false }),
    sportsSelection({
      selectionId,
      selectionOdds: odds?.decimalDisplayOdds?.decimalOdds ?? -1,
      selectionStatus: runnerStatus
    })
  ]);

  GAEvent({ action: 'Add Selection' }, context);
  buttonClick({ buttonName: 'Add Selection' }, context);
};

export type BetslipClearedProps = SingleOpportunity[];
export const betslipCleared = (
  context: TrackingContextValue,
  singleOpportunities: BetslipClearedProps
) => {
  context = mergeWithTrackingContextValue(
    context,
    singleOpportunities.flatMap((single) => {
      const selectionOdds = isAvailableOpportunity(single)
        ? single.winRunnerOdds.decimalDisplayOdds?.decimalOdds
        : undefined;

      const selectionEntity = sportsSelection({
        selectionId: single.runnerId.selectionId,
        selectionOdds: selectionOdds ?? 0,
        selectionStatus: getRunnerStatus(single.status)
      });
      const marketEntity = sportsMarket({
        marketId: Number(single.runnerId.marketId),
        marketInPlay: false,
        marketIsOddsBoost: false
      });
      const eventEntity = sportsEvent({
        eventId: Number(single.eventId),
        eventStartTime: new Date(single.eventStartTime).getTime()
      });

      return [selectionEntity, marketEntity, eventEntity];
    })
  );

  GAEvent({ action: 'Clear Bet Slip' }, context);
  buttonClick({ buttonName: 'Clear Bet Slip' }, context);
};

export interface SelectionRemovedProps {
  singleOpportunities: SingleOpportunity[];
  runnerId: RunnerId;
}
export const selectionRemoved = (
  context: TrackingContextValue,
  { singleOpportunities, runnerId }: SelectionRemovedProps
) => {
  const runnerStatusMap: Record<OpportunityStatus, RunnerStatus> = {
    AVAILABLE: RunnerStatus.ACTIVE,
    SUSPENDED: RunnerStatus.SUSPENDED,
    UNAVAILABLE: RunnerStatus.REMOVED
  };
  const opportunity = singleOpportunities.find((opp) => isEqual(opp.runnerId, runnerId));
  if (opportunity) {
    context = mergeWithTrackingContextValue(context, [
      sportsEvent({
        eventId: opportunity.eventId,
        eventStartTime: new Date(opportunity.eventStartTime).getTime()
      }),
      sportsMarket({
        marketId: Number(runnerId.marketId),
        marketInPlay: isAvailableOpportunity(opportunity) ? opportunity.eventInPlay : false,
        marketIsOddsBoost: isAvailableOpportunity(opportunity)
          ? isOddsBoostMarket(opportunity.marketType)
          : false
      }),
      sportsSelection({
        selectionId: runnerId.selectionId,
        selectionOdds: isAvailableOpportunity(opportunity)
          ? opportunity.winRunnerOdds.decimalDisplayOdds?.decimalOdds ?? -1
          : 0,
        selectionStatus: runnerStatusMap[opportunity.status]
      })
    ]);

    GAEvent({ action: 'Remove Selection' }, context);
    buttonClick({ buttonName: 'Remove Selection' }, context);
  }
};

const createSportsBetEntity = (
  result: PlaceBetResult,
  bettableOpportunities: BettableOpportunities,
  currency: Currency
) => {
  const { betId, totalStake, totalPotentialWin, betPlacedTime, runners } = result;

  if (!betId || !totalStake || !totalPotentialWin || !betPlacedTime || !runners) {
    return null;
  }

  const isMultipleBet = runners.length > 1;

  const betType = isMultipleBet
    ? bettableOpportunities.MULTIPLE.find(({ runnersIds }) =>
        isEqual(map(runnersIds, 'selectionId'), map(runners, 'runner.selectionId'))
      )?.type
    : bettableOpportunities.SINGLE.find(({ runnerId }) =>
        runners.find(({ runner }) => runner.selectionId === runnerId.selectionId)
      )?.type;

  return sportsBet({
    betCurrency: currency as SportsBet['betCurrency'],
    betType: betType as SportsBet['betType'],
    betId,
    betStake: totalStake,
    betPotentialReturn: totalPotentialWin,
    betPlacedTime: new Date(betPlacedTime).getTime(),
    betCashoutAvailable: false,
    betCashedOut: false
  });
};

interface BetPlacedProps {
  response: PlaceBetResp;
  bettableOpportunities: BettableOpportunities;
  opportunities: SingleAvailableOpportunity[];
  currency: Currency;
}
export const betPlaced = (
  context: TrackingContextValue,
  { response, bettableOpportunities, opportunities, currency }: BetPlacedProps
) => {
  const entities: Entity[] = [
    ...createSingleEntities(bettableOpportunities.SINGLE),
    ...createMultipleEntities(opportunities)(bettableOpportunities.MULTIPLE)
  ];

  response.result?.forEach((result) => {
    const sportsBetEntity: Entity | null = createSportsBetEntity(
      result,
      bettableOpportunities,
      currency
    );

    if (sportsBetEntity) entities.push(sportsBetEntity);
  });

  const betPlacedContext = {
    category: context.category,
    widget: merge(context.widget, { widgetContext: 'BET_RECEIPT' }),
    entities
  };

  GAEvent({ action: 'Bet placed' }, betPlacedContext);
  widgetView({}, betPlacedContext);
};

interface BetPlacedErrorProps {
  response: PlaceBetResp;
  bettableOpportunities: BettableOpportunities;
  opportunities: SingleAvailableOpportunity[];
}
export const betPlacementError = (
  context: TrackingContextValue,
  { response, bettableOpportunities, opportunities }: BetPlacedErrorProps
): void => {
  context = mergeWithTrackingContextValue(context, [
    ...createSingleEntities(bettableOpportunities.SINGLE),
    ...createMultipleEntities(opportunities)(bettableOpportunities.MULTIPLE)
  ]);

  if (response.respCode) {
    GAEvent({ action: response.respCode }, context);
    errorView({ errorCode: response.respCode }, context);
  }

  response.result?.forEach((result) => {
    GAEvent({ action: result.resultCode }, context);
    errorView({ errorCode: result.resultCode }, context);

    result.runners?.forEach((runner) => {
      if (runner.failureCode) {
        GAEvent({ action: runner.failureCode }, context);
        errorView({ errorCode: runner.failureCode }, context);
      }
    });
  });
};

interface BetPlacedExceptionProps {
  exception: RequestError;
  bettableOpportunities: BettableOpportunities;
  opportunities: SingleAvailableOpportunity[];
}
export const betPlacementException = (
  context: TrackingContextValue,
  { exception, bettableOpportunities, opportunities }: BetPlacedExceptionProps
) => {
  context = mergeWithTrackingContextValue(context, [
    ...createSingleEntities(bettableOpportunities.SINGLE),
    ...createMultipleEntities(opportunities)(bettableOpportunities.MULTIPLE)
  ]);

  try {
    const parsedException = JSON.parse(exception.body.code);

    GAEvent(
      { action: `(${parsedException.faultstring}) ${parsedException.detail.message}` },
      context
    );

    errorView(
      { errorCode: `(${parsedException.faultstring}) ${parsedException.detail.message}` },
      context
    );
  } catch (e) {
    GAEvent({ action: 'Bet placement failed' }, context);
    errorView({ errorCode: 'Bet placement failed' }, context);
  }
};

const betIsTrackable = ({ isSettled, stake }: MyBet) => !isSettled && stake !== undefined;
const isCashOutAvailable = (cashOutQuote?: CashOutQuote): cashOutQuote is AvailableCashOutQuote =>
  cashOutQuote !== undefined && cashOutQuote.quoteStatus === 'AVAILABLE';

interface OpenBetsProps {
  bets: MyBets['bets'];
  cashoutQuotes: MyBets['cashoutQuotes'];
}
export const openBets = (
  context: TrackingContextValue,
  { bets, cashoutQuotes }: OpenBetsProps
): void => {
  const { widget } = context;
  const entities: Entity[] = bets
    .filter(betIsTrackable)
    .flatMap(({ betType, betId, currency, stake, potentialWin, placedDate, result }) => {
      const cashOutQuote = cashoutQuotes[betId];
      const cashoutAvailable = isCashOutAvailable(cashOutQuote);

      return sportsBet({
        betCurrency: currency as SportsBet['betCurrency'],
        betType,
        betId: parseFloat(betId),
        betStake: stake as SportsBet['betStake'],
        betPotentialReturn: parseFloat(potentialWin),
        betPlacedTime: new Date(placedDate).getTime(),
        betCashoutPrice: cashoutAvailable ? cashOutQuote.quote : undefined,
        betCashoutAvailable: cashoutAvailable,
        betCashedOut: result === 'CASHED_OUT'
      });
    });

  if (widget) {
    entities.unshift(widgetEntity(widget));
  }

  if (entities.length > 0) {
    widgetView({}, mergeWithTrackingContextValue(context, entities));
  }
};
